import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { 
  Shield, 
  Users, 
  Settings, 
  Eye, 
  EyeOff, 
  Save,
  RotateCcw,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '@/components/auth/AuthProvider';
import { useNavigate } from 'react-router-dom';

interface PagePermission {
  id: string;
  name: string;
  path: string;
  staffAccess: boolean;
  adminAccess: boolean;
  description: string;
}

export const SecuritySettingsPage = () => {
  const { profile, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Only admin users should access this page
  if (!isAdmin) {
    navigate('/admin-dashboard');
    return null;
  }

  // Sample page permissions - in real app, this would come from database
  const [pagePermissions, setPagePermissions] = useState<PagePermission[]>([
    {
      id: 'dashboard',
      name: 'Staff Dashboard',
      path: '/staff-dashboard',
      staffAccess: true,
      adminAccess: true,
      description: 'Main staff interface for daily operations'
    },
    {
      id: 'admin-dashboard',
      name: 'Admin Dashboard',
      path: '/admin-dashboard',
      staffAccess: false,
      adminAccess: true,
      description: 'Administrative interface with full system access'
    },
    {
      id: 'analytics',
      name: 'Analytics',
      path: '/analytics',
      staffAccess: false,
      adminAccess: true,
      description: 'Business analytics and reporting tools'
    },
    {
      id: 'user-management',
      name: 'User Management',
      path: '/hr/user-management',
      staffAccess: false,
      adminAccess: true,
      description: 'Manage system users and their roles'
    },
    {
      id: 'sync-manager',
      name: 'Sync Manager',
      path: '/sync-manager',
      staffAccess: false,
      adminAccess: true,
      description: 'Data synchronization management'
    },
    {
      id: 'ai-features',
      name: 'AI Features',
      path: '/ai-features',
      staffAccess: false,
      adminAccess: true,
      description: 'Advanced AI-powered tools and automation'
    }
  ]);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const handlePermissionChange = (pageId: string, level: 'staff' | 'admin', value: boolean) => {
    setPagePermissions(prev => 
      prev.map(page => 
        page.id === pageId 
          ? { 
              ...page, 
              [level === 'staff' ? 'staffAccess' : 'adminAccess']: value 
            }
          : page
      )
    );
    setHasUnsavedChanges(true);
  };

  const handleSave = () => {
    // In real app, this would save to database
    console.log('Saving permissions:', pagePermissions);
    setHasUnsavedChanges(false);
    // Show success toast or notification
  };

  const handleReset = () => {
    // Reset to default values - in real app, fetch from database
    setHasUnsavedChanges(false);
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto space-y-8">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Shield className="w-8 h-8 text-red-600" />
              Security Settings
            </h1>
            <p className="text-muted-foreground">
              Configure page access permissions for different security levels
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <Badge variant="destructive">Admin Only</Badge>
            <Button variant="outline" onClick={() => navigate('/admin-dashboard')}>
              Back to Dashboard
            </Button>
          </div>
        </div>

        {/* Security Warning */}
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="space-y-1">
                <h4 className="font-medium text-amber-800">Important Security Notice</h4>
                <p className="text-sm text-amber-700">
                  Changes to security permissions affect all users immediately. 
                  Ensure you understand the implications before modifying access levels.
                  These settings only control UI visibility - server-side permissions remain enforced.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Permission Matrix */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Page Access Permissions
              </CardTitle>
              <div className="flex items-center gap-2">
                {hasUnsavedChanges && (
                  <Badge variant="outline" className="text-amber-600 border-amber-600">
                    Unsaved Changes
                  </Badge>
                )}
                <Button variant="outline" size="sm" onClick={handleReset}>
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Reset
                </Button>
                <Button size="sm" onClick={handleSave} disabled={!hasUnsavedChanges}>
                  <Save className="w-4 h-4 mr-1" />
                  Save Changes
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              
              {/* Column Headers */}
              <div className="grid grid-cols-12 gap-4 items-center font-medium text-sm border-b pb-2">
                <div className="col-span-6">Page / Feature</div>
                <div className="col-span-2 text-center">Staff Access</div>
                <div className="col-span-2 text-center">Admin Access</div>
                <div className="col-span-2 text-center">Actions</div>
              </div>

              {/* Permission Rows */}
              {pagePermissions.map((page) => (
                <div key={page.id} className="grid grid-cols-12 gap-4 items-center py-3 border-b">
                  <div className="col-span-6">
                    <div className="space-y-1">
                      <h4 className="font-medium">{page.name}</h4>
                      <p className="text-sm text-muted-foreground">{page.description}</p>
                      <code className="text-xs bg-muted px-2 py-1 rounded">{page.path}</code>
                    </div>
                  </div>
                  
                  <div className="col-span-2 flex justify-center">
                    <Switch
                      checked={page.staffAccess}
                      onCheckedChange={(value) => handlePermissionChange(page.id, 'staff', value)}
                    />
                  </div>
                  
                  <div className="col-span-2 flex justify-center">
                    <Switch
                      checked={page.adminAccess}
                      onCheckedChange={(value) => handlePermissionChange(page.id, 'admin', value)}
                      disabled={true} // Admins always have access
                    />
                  </div>
                  
                  <div className="col-span-2 flex justify-center gap-2">
                    <Button variant="ghost" size="sm">
                      {page.staffAccess ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Role Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Security Level Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Staff Level Settings */}
              <div className="space-y-4">
                <h3 className="font-medium text-blue-700">Staff Security Level</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="staff-session-timeout">Session Timeout (minutes)</Label>
                    <Input 
                      id="staff-session-timeout" 
                      type="number" 
                      defaultValue="60" 
                      className="w-20"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="staff-require-2fa">Require 2FA</Label>
                    <Switch id="staff-require-2fa" />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="staff-audit-logging">Audit Logging</Label>
                    <Switch id="staff-audit-logging" defaultChecked />
                  </div>
                </div>
              </div>

              {/* Admin Level Settings */}
              <div className="space-y-4">
                <h3 className="font-medium text-red-700">Admin Security Level</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="admin-session-timeout">Session Timeout (minutes)</Label>
                    <Input 
                      id="admin-session-timeout" 
                      type="number" 
                      defaultValue="30" 
                      className="w-20"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="admin-require-2fa">Require 2FA</Label>
                    <Switch id="admin-require-2fa" defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="admin-audit-logging">Enhanced Audit Logging</Label>
                    <Switch id="admin-audit-logging" defaultChecked />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};